import { useEffect, useState } from "react";
import { Button, Modal } from "semantic-ui-react";
import { FormError } from "../models/FormError";
import EditPermitTemplateForm, { PermitTemplateFormData, TemplateField } from "./EditPermitTemplateForm";
import {
  GisBoundaryFeature,
  AsBuiltFeature,
  GangMembersFeature,
  SitePhotoFeature,
  AffectedServicesFeature,
  PermitConfiguration,
} from "../models/PermitFeatures";
import { removeTypenameDeep } from "../utils/apollo";
import { PermitTemplate } from "../models/PermitTemplate";

/** Common shape for any entity that has a name and a PermitTemplate (blueprint or permit type). */
export interface PermitTemplateEntity {
  id: string;
  name: string;
  template?: PermitTemplate | null;
}

interface AddProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (name: string, templateJson?: string) => void;
  submitInProgress: boolean;
  submitErrors?: string[];
  entity?: undefined;
  entityLabel?: string;
}

interface EditProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (name: string, id: string, templateJson?: string) => void;
  submitInProgress: boolean;
  submitErrors?: string[];
  entity: PermitTemplateEntity;
  entityLabel?: string;
}

type Props = AddProps | EditProps;

const defaultGisBoundary: GisBoundaryFeature = {
  enabled: false,
  geofencingStatus: { value: "disabled", options: ["disabled", "warning", "enforced"] },
  geofencingToleranceMeters: 50,
};

const defaultAsBuilt: AsBuiltFeature = {
  enabled: false,
  asBuiltCompletionDeclarations: [],
};

const defaultGangMembers: GangMembersFeature = {
  enabled: false,
  permitMustHaveGangMembersToIssue: false,
  briefingCheckListItems: [],
  briefingDeclaration: "",
};

const defaultSitePhoto: SitePhotoFeature = {
  enabled: false,
  requireSitePhotoForSubmission: false,
  requireSitePhotoForRequest: false,
};

const defaultAffectedServices: AffectedServicesFeature = {
  enabled: false,
};

const defaultConfiguration: PermitConfiguration = {
  preRequestDeclarations: [],
  preSubmissionDeclarations: [],
  optionToSelectThePermitTeamInThePermitRequest: false,
  maxPermitDurationDays: null,
  maxPermitValidInAdvanceDays: null,
};

const emptyFormData: PermitTemplateFormData = {
  name: "",
  fields: [],
  catAndGennyEnabled: false,
  gisBoundary: defaultGisBoundary,
  asBuilt: defaultAsBuilt,
  gangMembers: defaultGangMembers,
  sitePhoto: defaultSitePhoto,
  affectedServices: defaultAffectedServices,
  configuration: defaultConfiguration,
};

const templateToFields = (template: any): TemplateField[] => {
  if (!template?.fields) return [];
  return [...template.fields]
    .sort((a: any, b: any) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .map((f: any) => ({
      key: f.key,
      label: f.label,
      fieldType: f.fieldType,
      // Text
      maxLength: f.maxLength,
      isMultiLine: f.isMultiLine,
      // Boolean
      defaultValue: f.defaultValue,
      trueLabel: f.trueLabel,
      falseLabel: f.falseLabel,
      // Number
      minValue: f.minValue,
      maxValue: f.maxValue,
      decimalPlaces: f.decimalPlaces,
      unit: f.unit,
      // Date
      includeTime: f.includeTime,
      // Stage config
      requiredForState: f.requiredForState ?? null,
      editableAtStages: f.editableAtStages ?? [],
    }));
};

const formDataToTemplateJson = (data: PermitTemplateFormData, forceSerialize = false): string | undefined => {
  if (
    !forceSerialize &&
    data.fields.length === 0 &&
    !data.catAndGennyEnabled &&
    !data.gisBoundary.enabled &&
    !data.asBuilt.enabled &&
    !data.gangMembers.enabled &&
    !data.sitePhoto.enabled &&
    !data.affectedServices.enabled &&
    (data.configuration.preRequestDeclarations?.length ?? 0) === 0 &&
    (data.configuration.preSubmissionDeclarations?.length ?? 0) === 0
  ) {
    return undefined;
  }
  const template = {
    fields: data.fields.map((f, i) => {
      const base: any = {
        key: f.key,
        label: f.label,
        fieldType: f.fieldType,
        displayOrder: i + 1,
        requiredForState: f.requiredForState || null,
        editableAtStages: f.editableAtStages ?? [],
      };
      switch (f.fieldType) {
        case "Text":
          if (f.maxLength != null) base.maxLength = f.maxLength;
          if (f.isMultiLine) base.isMultiLine = true;
          break;
        case "Number":
          if (f.minValue != null) base.minValue = f.minValue;
          if (f.maxValue != null) base.maxValue = f.maxValue;
          if (f.decimalPlaces != null) base.decimalPlaces = f.decimalPlaces;
          if (f.unit) base.unit = f.unit;
          break;
        case "Boolean":
          base.defaultValue = f.defaultValue ?? null;
          if (f.trueLabel) base.trueLabel = f.trueLabel;
          if (f.falseLabel) base.falseLabel = f.falseLabel;
          break;
        case "Date":
          if (f.includeTime) base.includeTime = true;
          break;
      }
      return base;
    }),
    features: {
      catAndGenny: { enabled: data.catAndGennyEnabled },
      gisBoundary: {
        enabled: data.gisBoundary.enabled,
        geofencingStatus: data.gisBoundary.geofencingStatus,
        geofencingToleranceMeters: data.gisBoundary.geofencingToleranceMeters,
      },
      asBuilt: {
        enabled: data.asBuilt.enabled,
        asBuiltCompletionDeclarations: data.asBuilt.asBuiltCompletionDeclarations,
      },
      gangMembers: {
        enabled: data.gangMembers.enabled,
        permitMustHaveGangMembersToIssue: data.gangMembers.permitMustHaveGangMembersToIssue,
        briefingCheckListItems: data.gangMembers.briefingCheckListItems,
        briefingDeclaration: data.gangMembers.briefingDeclaration,
      },
      sitePhoto: {
        enabled: data.sitePhoto.enabled,
        requireSitePhotoForSubmission: data.sitePhoto.requireSitePhotoForSubmission,
        requireSitePhotoForRequest: data.sitePhoto.requireSitePhotoForRequest,
      },
      affectedServices: {
        enabled: data.affectedServices.enabled,
      },
    },
    configuration: {
      preRequestDeclarations: data.configuration.preRequestDeclarations,
      preSubmissionDeclarations: data.configuration.preSubmissionDeclarations,
      optionToSelectThePermitTeamInThePermitRequest: data.configuration.optionToSelectThePermitTeamInThePermitRequest,
      maxPermitDurationDays: data.configuration.maxPermitDurationDays,
      maxPermitValidInAdvanceDays: data.configuration.maxPermitValidInAdvanceDays,
    },
  };
  return JSON.stringify(template);
};

const EditPermitTemplateModal = ({
  open,
  onClose,
  onSubmit,
  submitInProgress,
  submitErrors,
  entity,
  entityLabel = "Blueprint",
}: Props) => {
  const [formData, setFormData] = useState<PermitTemplateFormData>(emptyFormData);
  const [formErrors, setFormErrors] = useState<FormError>({});

  useEffect(() => {
    if (open) {
      if (entity) {
        const template = entity.template ? removeTypenameDeep(entity.template) : null;
        const features = template?.features;
        setFormData({
          id: entity.id,
          name: entity.name,
          fields: templateToFields(template),
          catAndGennyEnabled: features?.catAndGenny?.enabled ?? false,
          gisBoundary: {
            enabled: features?.gisBoundary?.enabled ?? false,
            geofencingStatus: features?.gisBoundary?.geofencingStatus ?? defaultGisBoundary.geofencingStatus,
            geofencingToleranceMeters: features?.gisBoundary?.geofencingToleranceMeters ?? 50,
          },
          asBuilt: {
            enabled: features?.asBuilt?.enabled ?? false,
            asBuiltCompletionDeclarations: features?.asBuilt?.asBuiltCompletionDeclarations ?? [],
          },
          gangMembers: {
            enabled: features?.gangMembers?.enabled ?? false,
            permitMustHaveGangMembersToIssue: features?.gangMembers?.permitMustHaveGangMembersToIssue ?? false,
            briefingCheckListItems: features?.gangMembers?.briefingCheckListItems ?? [],
            briefingDeclaration: features?.gangMembers?.briefingDeclaration ?? "",
          },
          sitePhoto: {
            enabled: features?.sitePhoto?.enabled ?? false,
            requireSitePhotoForSubmission: features?.sitePhoto?.requireSitePhotoForSubmission ?? false,
            requireSitePhotoForRequest: features?.sitePhoto?.requireSitePhotoForRequest ?? false,
          },
          affectedServices: {
            enabled: features?.affectedServices?.enabled ?? false,
          },
          configuration: {
            preRequestDeclarations: template?.configuration?.preRequestDeclarations ?? [],
            preSubmissionDeclarations: template?.configuration?.preSubmissionDeclarations ?? [],
            optionToSelectThePermitTeamInThePermitRequest:
              template?.configuration?.optionToSelectThePermitTeamInThePermitRequest ?? false,
            maxPermitDurationDays: template?.configuration?.maxPermitDurationDays ?? null,
            maxPermitValidInAdvanceDays: template?.configuration?.maxPermitValidInAdvanceDays ?? null,
          },
        });
      } else {
        setFormData(emptyFormData);
      }
    } else {
      setFormData(emptyFormData);
      setFormErrors({});
    }
  }, [open, entity]);

  const validate = (data: PermitTemplateFormData): FormError => {
    const errors: FormError = {};
    if (!data.name) errors.name = "Name is required";
    const keys = new Set<string>();
    data.fields.forEach((field, index) => {
      if (!field.label) errors[`field_${index}_label`] = "Label is required";
      if (field.key && keys.has(field.key)) errors[`field_${index}_label`] = "Duplicate field label";
      else if (field.key) keys.add(field.key);
    });
    return errors;
  };

  const handleSubmit = () => {
    const errors = validate(formData);
    setFormErrors(errors);
    if (Object.keys(errors).length === 0) {
      const templateJson = formDataToTemplateJson(formData, !!entity);
      if (entity) {
        (onSubmit as (name: string, id: string, templateJson?: string) => void)(formData.name, entity.id, templateJson);
      } else {
        (onSubmit as (name: string, templateJson?: string) => void)(formData.name, templateJson);
      }
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="large">
      <Modal.Header>{entity ? `Edit ${entityLabel}` : `Add ${entityLabel}`}</Modal.Header>
      <Modal.Content scrolling>
        <EditPermitTemplateForm
          formData={formData}
          formErrors={formErrors}
          errors={submitErrors}
          onChange={setFormData}
        />
      </Modal.Content>
      <Modal.Actions style={{ display: "flex", justifyContent: "flex-end", gap: "0.5em" }}>
        <Button onClick={onClose} disabled={submitInProgress} basic compact size="small">
          Cancel
        </Button>
        <Button
          primary
          onClick={handleSubmit}
          loading={submitInProgress}
          disabled={submitInProgress}
          compact
          size="small"
        >
          {entity ? "Save" : "Add"}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default EditPermitTemplateModal;
